import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import React, { useContext, useEffect, useState } from 'react';
import {
  MappingAzureOperatorType,
  MappingAzureSubOperatorType,
  MappingAzureUser,
  SdmTextfieldV5,
} from 'pages/ticketV2/common';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import it from 'react-phone-input-2/lang/it.json';
import { SdmSelectV5 } from 'components/SdmSelectV5';
import { SdmCheckboxV5 } from 'components/SdmCheckboxV5';
import { SdmSwitchV5 } from 'components/SdmSwitchV5';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import {
  enNations,
  getRoleOptions,
  nations,
  rolesInCompanyOthersValues,
} from 'pages/tickets/components/TicketsEdit/constants';
import { useRef } from 'react';
import { useNetwork } from 'services/network';
import { LoaderCountContext } from 'context/LoaderV5';
import { useDebounce } from 'utils/hooks';
import { CartContext } from 'context/CartContextProvider';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

const baseCssClass = 'react-telephone-input';

const DOMNIA_CITIES_PAGINATION = 30;

const emailRegex =
  /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const textfieldInputKeys = [
  'firstname',
  'lastname',
  'email',
  'phone',
  'sendToThisEmail',
  'company',
];

const ContainerBody = ({
  currentTicket,
  locale,
  isOperator,
  form,
  loggedUser,
  assignTicketTexts,
}) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Tickets(intl).edit.ticketEditCard;
  const countryRef = useRef();
  const network = useNetwork();

  const { setLoaderActive } = useContext(LoaderCountContext);
  const { customer } = useContext(CartContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  const [cityOptions, setCityOptions] = useState([]);
  const [fieldsToDisable, setFieldToDisable] = useState([]);
  const [emptyCityOptions, setEmptyCityOptions] = useState(false);
  const [characters, setCharacters] = useState('');
  const debouncedText = useDebounce(characters, 500);
  const wCountry = form.watch('country');
  const wCity = form.watch('city');
  const wOperatorType = form.watch('sdm_operator_type');

  const mediaTheme = useTheme();
  const isLgUp = useMediaQuery(mediaTheme.breakpoints.up('lg'));
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const isIphone5 = useMediaQuery(mediaTheme.breakpoints.down(376));
  const [loggedDataAtIndex, stepperCurrentTicketIdx] = useTicketsStore(
    (state) => [
      state.loggedDataAtIndex,
      state.stepperCurrentTicketIdx,
      state.ticketsData,
    ]
  );

  const { errors, control, setValue, watch, getValues } = form;

  const parsedCurrentTicket = {
    ...currentTicket,
    isStudent: currentTicket?.code?.includes('_STUD_'),
    isPrd: currentTicket?.code?.includes('PRD_'),
  };

  useEffect(() => {
    const getOptions = async () => {
      if (debouncedText && debouncedText.length >= 2) {
        setCityOptions([]);
        showLoader();
        const selectedNation = getValues('country').value;
        const citiesRes = await network.ticketingLambda({
          params: `/addresses/city?country_iso3=${selectedNation}&page=0&size=${DOMNIA_CITIES_PAGINATION}&name=${debouncedText}`,
          customMethod: 'get',
          data: {
            params: `/addresses/city?country_iso3=${selectedNation}&page=0&size=${DOMNIA_CITIES_PAGINATION}&name=${debouncedText}`,
            customMethod: 'get',
            data: {},
          },
          federationId: loggedUser?.salId,
        });
        const parsedCities = citiesRes.map((city) => ({
          value: city.name,
          label: city.name,
        }));
        setCityOptions(parsedCities);
        hideLoader();
      } else {
        setCityOptions([]);
      }
    };
    getOptions();
  }, [debouncedText]);

  useEffect(() => {
    if (emptyCityOptions || !wCountry) emptyCities();
  }, [emptyCityOptions, wCountry]);

  useEffect(() => {
    if (wCity && !cityOptions.find((c) => c.value === wCity.value)) {
      setCharacters(wCity.value);
    }
  }, [wCity]);

  useEffect(() => {
    if (wOperatorType) {
      emptyOperatorInfo();
    }
  }, [wOperatorType]);

  const emptyCities = () => {
    setCityOptions([]);
    form.setValue('city', '');
    setEmptyCityOptions(false);
  };

  const emptyOperatorInfo = () => {
    if (
      form.getValues('sdm_role_in_company') &&
      !getRoleOptions(i18n.fields.types.sdm_role_in_company)?.[
        wOperatorType
      ]?.find((role) => role.value === form.getValues('sdm_role_in_company'))
    ) {
      form.setValue('sdm_role_in_company', '');
    }
    if (
      form.getValues('sdm_operator_sub_type') &&
      !subOperatorTypeOptions[wOperatorType]?.find(
        (sub) => sub.value === form.getValues('sdm_operator_sub_type')
      )
    ) {
      form.setValue('sdm_operator_sub_type', '');
    }
    form.setValue('sdm_other_role_in_company', '');
    form.setValue('sdm_other_type', '');
  };

  const operatorTypeOptions = [
    { label: i18n.fields.types.adp, value: 'adp' },
    { label: i18n.fields.types.apa, value: 'apa' },
    { label: i18n.fields.types.ret, value: 'ret' },
    { label: i18n.fields.types.acq, value: 'acq' },
    { label: i18n.fields.types.grs, value: 'grs' },
    { label: i18n.fields.types.frn, value: 'frn' },
    { label: i18n.fields.types.agn, value: 'agn' },
    { label: i18n.fields.types.prd, value: 'prd' },
    { label: i18n.fields.types.mrk, value: 'mrk' },
    { label: i18n.fields.types.prv, value: 'prv' },
  ];

  const subOperatorTypeOptions = {
    adp: [
      { label: i18n.fields.types.adp_arc, value: 'adp_arc' },
      { label: i18n.fields.types.adp_dsn, value: 'adp_dsn' },
      { label: i18n.fields.types.adp_int, value: 'adp_int' },
      { label: i18n.fields.types.adp_lig, value: 'adp_lig' },
      { label: i18n.fields.types.adp_geo, value: 'adp_geo' },
      { label: i18n.fields.types.adp_oth, value: 'adp_oth' },
    ],
    apa: [
      { label: i18n.fields.types.apa_dds, value: 'apa_dds' },
      { label: i18n.fields.types.apa_gnc, value: 'apa_gnc' },
      { label: i18n.fields.types.apa_svl, value: 'apa_svl' },
      { label: i18n.fields.types.apa_oth, value: 'apa_oth' },
    ],
    ret: [
      { label: i18n.fields.types.ret_bou, value: 'ret_bou' },
      { label: i18n.fields.types.ret_cat, value: 'ret_cat' },
      { label: i18n.fields.types.ret_dep, value: 'ret_dep' },
      { label: i18n.fields.types.ret_gac, value: 'ret_gac' },
      { label: i18n.fields.types.ret_oth, value: 'ret_oth' },
    ],
    frn: [
      { label: i18n.fields.types.frn_fdc, value: 'frn_fdc' },
      { label: i18n.fields.types.frn_fdm, value: 'frn_fdm' },
      { label: i18n.fields.types.frn_fds, value: 'frn_fds' },
    ],
    grs: [
      { label: i18n.fields.types.grs_dsr, value: 'grs_dsr' },
      { label: i18n.fields.types.grs_exp, value: 'grs_exp' },
      { label: i18n.fields.types.grs_imp, value: 'grs_imp' },
      { label: i18n.fields.types.grs_oth, value: 'grs_oth' },
    ],
    prd: [
      { label: i18n.fields.types.prd_ard, value: 'prd_ard' },
      { label: i18n.fields.types.prd_aru, value: 'prd_aru' },
      { label: i18n.fields.types.prd_arb, value: 'prd_arb' },
      { label: i18n.fields.types.prd_cda, value: 'prd_cda' },
      { label: i18n.fields.types.prd_arc, value: 'prd_arc' },
      { label: i18n.fields.types.prd_ele, value: 'prd_ele' },
      { label: i18n.fields.types.prd_lig, value: 'prd_lig' },
      { label: i18n.fields.types.prd_oth, value: 'prd_oth' },
    ],
    acq: [
      { label: i18n.fields.types.acq_sdb, value: 'acq_sdb' },
      { label: i18n.fields.types.acq_ret, value: 'acq_ret' },
      { label: i18n.fields.types.acq_who, value: 'acq_who' },
      { label: i18n.fields.types.acq_dps, value: 'acq_dps' },
      { label: i18n.fields.types.acq_gda, value: 'acq_gda' },
      { label: i18n.fields.types.acq_oth, value: 'acq_oth' },
    ],
  };

  // const ticketHasAnOwnerName = currentTicket.ownerName || watch('firstname');

  const handleSubValidation = () => {
    const oT = watch('sdm_operator_type');
    const _ot = parsedCurrentTicket?.operatorType;
    const ticketHasOperatorType =
      (_ot && _ot !== 'mrk' && _ot !== 'agn' && _ot !== 'prv') ||
      (oT && oT !== 'mrk' && oT !== 'agn' && oT !== 'prv');
    return ticketHasOperatorType;
  };

  const handleRoleValidation = () => {
    const oT = watch('sdm_operator_type');
    const _ot = parsedCurrentTicket?.operatorType;
    const ticketHasOperatorType =
      (_ot && _ot !== 'mrk' && _ot !== 'acq') ||
      (oT && oT !== 'mrk' && oT !== 'acq');
    return ticketHasOperatorType;
  };

  const ticketHasSubOperator =
    parsedCurrentTicket?.subOperatorType?.includes('_oth') ||
    (watch('sdm_operator_sub_type') &&
      watch('sdm_operator_sub_type').includes('_oth'));

  const ticketHasRoleType =
    (parsedCurrentTicket?.role &&
      !rolesInCompanyOthersValues.includes(parsedCurrentTicket?.role)) ||
    (watch('sdm_role_in_company') &&
      rolesInCompanyOthersValues.includes(watch('sdm_role_in_company')));

  const addLoggedDataToTicket = async (checked) => {
    useTicketsStore.setState({
      loggedDataAtIndex: checked ? stepperCurrentTicketIdx : null,
    });

    // const { email, givenName, familyName } = loggedUser;
    // setValue('email', checked ? email : '');
    // setValue('firstname', checked ? givenName : '');
    // setValue('lastname', checked ? familyName : '');
    if (!checked) {
      setValue('owner_flag_privacy_1', '3');
      setValue('owner_flag_privacy_2', '3');
      setValue('owner_flag_privacy_3', '3');
    }

    if (customer && customer?.accountid) {
      const mappedUser = {};

      const getOperatorType = (azureKey) =>
        Object.entries(MappingAzureOperatorType).find(
          ([, azu]) => azu === customer[azureKey]
        )?.[0] || '';

      Object.entries(MappingAzureUser).map(([azureKey, develonKey]) => {
        switch (azureKey) {
          case 'tipologia':
            mappedUser[develonKey] = getOperatorType(azureKey);
            break;
          case 'sottoTipologia':
            mappedUser[develonKey] =
              customer['tipologia'] &&
              MappingAzureSubOperatorType[getOperatorType('tipologia')]
                ? Object.entries(
                    MappingAzureSubOperatorType[getOperatorType('tipologia')]
                  ).find(([, azu]) => azu === customer[azureKey])?.[0] || ''
                : '';
            break;
          case 'personMailingCountry':
            mappedUser[develonKey] = customer[azureKey]
              ? locale === 'en'
                ? enNations.find((n) => n.value === customer[azureKey])
                : nations.find((n) => n.value === customer[azureKey])
              : '';
            break;
          case 'role':
            mappedUser[develonKey] = customer?.role
              ? parseInt(customer.role)
              : '';
            break;
          default:
            mappedUser[develonKey] = customer[azureKey] || '';
            break;
        }
      });
      // Checking City => call Develon if it does not exist skip
      if (customer && customer.personMailingCity && checked) {
        const savedCity = customer.personMailingCity;
        const savedCountry = customer.personMailingCountry;
        if (savedCity && savedCountry) {
          showLoader();
          const citiesRes = await network.ticketingLambda({
            params: `/addresses/city?country_iso3=${savedCountry}&name=${savedCity}`,
            customMethod: 'get',
            data: {
              params: `/addresses/city?country_iso3=${savedCountry}&name=${savedCity}`,
              customMethod: 'get',
              data: {},
            },
            federationId: loggedUser?.salId,
          });
          const foundinDevelon = citiesRes.find(
            (city) => `${city?.name}`?.toLowerCase() === savedCity.toLowerCase()
          );
          if (foundinDevelon) {
            mappedUser.city = foundinDevelon.name;
          } else {
            mappedUser.city = '';
            emptyCities();
          }
          hideLoader();
        }
      }
      if (customer && !customer.personMailingCity && checked) {
        mappedUser.city = '';
        emptyCities();
      }

      Object.entries(mappedUser).map(([key, value]) =>
        setValue(key, checked ? value : '')
      );
      const toDisable = Object.entries(mappedUser)
        .filter(([, value]) => !!value)
        .map(([key]) => key);
      setFieldToDisable(checked ? toDisable : []);
    }
  };

  // Renders
  const renderError = (key, labelKey, marginTop = 0) => {
    if (errors[key]) {
      let errorMessage;

      if (errors[key]?.type === 'required')
        errorMessage = i18n.fields[labelKey ? labelKey : key].error;

      if (
        errors[key]?.type === 'custom' ||
        errors[key]?.type === 'matchPattern'
      )
        errorMessage = errors[key]?.message;

      return (
        <Typography
          style={{
            marginTop: marginTop,
          }}
          variant={'body2'}
          color="primary"
          fontFamily={'Suisse-Intl'}
        >
          {errorMessage}
        </Typography>
      );
    }
    return null;
  };

  const renderInput = (props, field) => {
    switch (field) {
      case 'phone':
        return (
          <Grid item key={field} xs={12} md={6}>
            <label
              htmlFor="phone-input"
              style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                border: 0,
                zIndex: -10,
              }}
            >
              Phone
            </label>
            <PhoneInput
              country={locale === 'it' ? 'it' : 'us'}
              preferredCountries={[locale, 'us']}
              priority={{ it: 0, us: 1 }}
              name={field}
              enableSearch
              localization={locale === 'it' ? it : {}}
              searchPlaceholder={i18n.fields.phone.searchPlaceholder}
              searchNotFound={i18n.fields.phone.searchNotFound}
              specialLabel={i18n.fields.phone.label}
              containerClass={`${baseCssClass}-container${
                fieldsToDisable.includes(field) ? ' disabled' : ''
              }`}
              inputClass={`${baseCssClass}-input${
                fieldsToDisable.includes(field) ? ' disabled' : ''
              }`}
              dropdownClass={`${baseCssClass}-dropdown${
                fieldsToDisable.includes(field) ? ' disabled' : ''
              }`}
              onChange={(e) => {
                props.onChange(e);
              }}
              value={props.value}
              placeholder=""
              disabled={
                fieldsToDisable.includes(field) &&
                loggedDataAtIndex === stepperCurrentTicketIdx &&
                ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                  (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
              }
              inputProps={{
                name: field,
                required: true,
                id: 'phone-input',
              }}
            />
            {renderError(field)}
          </Grid>
        );
      case 'sendToThisEmail':
        return (
          <Grid item xs={12}>
            <SdmCheckboxV5
              onChange={(e) => {
                props.onChange(e.target.checked);
              }}
              variant="whiteOutlined"
              value={props.value}
              isSelected={props.value}
              label={i18n.fields[field]?.label}
              labelPlacement={'end'}
              customFont={'text'}
              style={{ marginLeft: 15 }}
            />
            {renderError(field)}
          </Grid>
        );
      default:
        return (
          <Grid item key={field} xs={12} md={6}>
            <SdmTextfieldV5
              variant="standard"
              onChange={(e) => {
                props.onChange(e.target.value);
              }}
              value={props.value}
              id={`${field}-required`}
              label={i18n.fields[field].label}
              isIphone5={isIphone5}
              disabled={
                fieldsToDisable.includes(field) &&
                loggedDataAtIndex === stepperCurrentTicketIdx &&
                ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                  (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
                // (field === 'firstname' ||
                //   field === 'lastname' ||
                //   field === 'email') &&
                // loggedDataAtIndex === stepperCurrentTicketIdx
              }
              InputProps={{
                ...(field === 'email' &&
                  errors &&
                  errors['email'] && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <ErrorOutlineOutlinedIcon color="primary" />
                      </InputAdornment>
                    ),
                  }),
              }}
              style={{ width: '100%' }}
            />
            {renderError(field)}
          </Grid>
        );
    }
  };

  const countryOptions = locale === 'en' ? enNations : nations;

  const validatePhoneOnHtml = (v) => {
    const trimmedPhone = v.trim();
    if (!trimmedPhone || trimmedPhone?.length < 7)
      return i18n.fields.phone.error;
  };

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {parsedCurrentTicket ? (
        <Stack
          gap={'25px'}
          sx={{
            p: '30px',
            boxShadow: '0px 0px 5px 0px rgba(51, 51, 51, 0.05)',
            bgcolor: 'white',
          }}
        >
          <Stack direction={'row'} alignItems={'center'} gap={'10px'}>
            {isLgUp ? (
              <div style={{ fontSize: '30px', paddingTop: '4px' }}>
                <PersonOutlineOutlinedIcon fontSize="inherit" />
              </div>
            ) : (
              <PersonOutlineOutlinedIcon fontSize="medium" sx={{ mb: '4px' }} />
            )}
            <Typography
              variant="m-bold"
              fontFamily={'Suisse-Intl'}
              component={'h2'}
            >
              {assignTicketTexts.ticketHolderFormTitle}
            </Typography>
          </Stack>
          {/* se è operatore, biglietto fornitore, ma l'utente non è fornitore, non può intestarselo */}
          {isOperator && parsedCurrentTicket?.isPrd && !loggedUser.isPrd ? (
            <></>
          ) : (
            <>
              {(!Number.isInteger(loggedDataAtIndex) ||
                loggedDataAtIndex === stepperCurrentTicketIdx) && (
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Typography variant="body1" component={'p'}>
                    {assignTicketTexts.ticketHolderFormSubtitle}
                  </Typography>

                  <Box sx={{ position: 'relative' }}>
                    <label
                      htmlFor="assign-switch"
                      style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                        zIndex: -10,
                      }}
                    >
                      {assignTicketTexts.ticketHolderFormSubtitle}
                    </label>

                    <SdmSwitchV5
                      id="assign-switch"
                      style={{ marginBottom: 10 }}
                      onChange={(e) => addLoggedDataToTicket(e.target.checked)}
                      checked={
                        loggedDataAtIndex === stepperCurrentTicketIdx &&
                        ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                          (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd)) &&
                        !!getValues('firstname')
                      }
                    />
                  </Box>
                </Stack>
              )}
            </>
          )}

          {/* <form onSubmit={handleSubmit(customSubmit)}> */}
          <Grid
            container
            style={{ width: isSmDown ? '105%' : '103%' }}
            rowGap={'20px'}
            columnSpacing={'20px'}
          >
            <a
              id="data-form"
              style={{
                position: 'relative',
                top: '-130px',
                visibility: 'hidden',
              }}
            />
            {textfieldInputKeys
              .filter((i) => i !== 'company')
              .map((field) => (
                <>
                  <Controller
                    name={field}
                    control={control}
                    defaultValue=""
                    rules={{
                      required: field !== 'sendToThisEmail',
                      ...(field === 'email' && {
                        validate: {
                          matchPattern: (v) => {
                            if (!emailRegex.test(v)) {
                              return 'Formato email non valido';
                            }
                          },
                        },
                      }),
                      ...(field === 'phone' && {
                        validate: {
                          matchPattern: (v) => validatePhoneOnHtml(v),
                        },
                      }),
                    }}
                    render={(props) => renderInput(props, field)}
                  />
                </>
              ))}
            <Grid item xs={12} md={6}>
              <Controller
                name={'country'}
                control={control}
                rules={{ required: true }}
                defaultValue={''}
                render={(props) => (
                  <Autocomplete
                    {...props}
                    id={'country_id'}
                    ref={countryRef}
                    options={countryOptions}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.label
                    }
                    onChange={(e, item) => {
                      props.onChange(item);
                      setEmptyCityOptions(true);
                    }}
                    disabled={
                      fieldsToDisable.includes('country') &&
                      loggedDataAtIndex === stepperCurrentTicketIdx &&
                      ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                        (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
                    }
                    renderInput={(params) => (
                      <SdmTextfieldV5
                        {...params}
                        variant="standard"
                        label={i18n.fields.country.label}
                        value={props.value}
                        style={{ width: '100%' }}
                      />
                    )}
                  />
                )}
              />
              {renderError('country')}
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={'city'}
                control={control}
                rules={{ required: true }}
                defaultValue={''}
                render={(props) => (
                  <Autocomplete
                    {...props}
                    id={'city_id'}
                    options={cityOptions}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.label
                    }
                    onChange={(e, item) => {
                      props.onChange(item);
                    }}
                    onInputChange={(_event, value, reason) => {
                      /*  Pure Art 2 🎨 */
                      if (reason !== 'reset') {
                        setCharacters(value);
                      }
                    }}
                    disabled={
                      fieldsToDisable.includes('city') &&
                      loggedDataAtIndex === stepperCurrentTicketIdx &&
                      ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                        (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
                    }
                    renderInput={(params) => (
                      <SdmTextfieldV5
                        {...params}
                        variant="standard"
                        label={i18n.fields.city.label}
                        value={props.value}
                        style={{ width: '100%' }}
                      />
                    )}
                  />
                )}
              />
              {renderError('city')}
            </Grid>
            {isOperator ? (
              <>
                {textfieldInputKeys
                  .filter((i) => i === 'company')
                  .map((field) => (
                    <>
                      <Controller
                        name={field}
                        control={control}
                        defaultValue=""
                        rules={{
                          required: true,
                        }}
                        render={(props) => renderInput(props, field)}
                      />
                    </>
                  ))}
              </>
            ) : null}
            {isOperator ? (
              <Grid item xs={12} md={6}>
                <Controller
                  name={'sdm_operator_type'}
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={(props) => (
                    <SdmSelectV5
                      options={operatorTypeOptions.filter((i) =>
                        parsedCurrentTicket?.isPrd
                          ? i.value === 'frn' || i.value === 'prd'
                          : isOperator
                          ? i.value !== 'prv' &&
                            i.value !== 'frn' &&
                            i.value !== 'prd'
                          : i
                      )}
                      value={props.value}
                      disabled={
                        parsedCurrentTicket?.isStudent ||
                        (fieldsToDisable.includes('sdm_operator_type') &&
                          loggedDataAtIndex === stepperCurrentTicketIdx &&
                          ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                            (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd)))
                      }
                      labelKey={'label'}
                      label={i18n.fields.sdm_operator_type.label}
                      disabledLabel={i18n.fields.sdm_operator_type.label}
                      onSelect={(e) => {
                        props.onChange(e);
                      }}
                      customStyle={{
                        width: '100%',
                        fontSize: 20,
                      }}
                      inputProps={{ 'aria-label': 'Operator Type' }}
                    />
                  )}
                />
                {renderError('sdm_operator_type')}
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <SdmTextfieldV5
                  variant="standard"
                  label={i18n.fields.sdm_operator_type.label}
                  disabled={true}
                  style={{ width: '100%' }}
                  value={
                    parsedCurrentTicket?.isStudent
                      ? i18n.fields.privateStudent
                      : i18n.fields.types.prv
                  }
                  inputProps={{
                    'aria-label': 'Operator Type',
                  }}
                />
              </Grid>
            )}
            {isOperator && (
              <>
                {handleRoleValidation() ? (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name={'sdm_role_in_company'}
                      control={control}
                      defaultValue=""
                      rules={{ required: true }}
                      render={(props) => (
                        <SdmSelectV5
                          options={
                            getRoleOptions(
                              i18n.fields.types.sdm_role_in_company
                            )?.[watch('sdm_operator_type')]
                          }
                          value={props.value}
                          labelKey={'label'}
                          label={i18n.fields.role.label}
                          disabledLabel={i18n.fields.role.label}
                          onSelect={(e) => {
                            props.onChange(e.target.value);
                          }}
                          customStyle={{
                            width: '100%',
                            fontSize: isIphone5 ? 16 : 20,
                          }}
                          disabled={
                            fieldsToDisable.includes('sdm_role_in_company') &&
                            loggedDataAtIndex === stepperCurrentTicketIdx &&
                            ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                              (!parsedCurrentTicket?.isPrd &&
                                !loggedUser.isPrd))
                          }
                          inputProps={{
                            'aria-label': 'role in company',
                          }}
                        />
                      )}
                    />
                    {renderError('sdm_role_in_company', 'role')}
                  </Grid>
                ) : null}
              </>
            )}
            {ticketHasRoleType && (
              <Grid item xs={12} md={6}>
                <Controller
                  name={'sdm_other_role_in_company'}
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={(props) => (
                    <SdmTextfieldV5
                      variant="standard"
                      style={{ width: '100%' }}
                      onChange={(e) => {
                        props.onChange(e.target.value);
                      }}
                      value={props.value}
                      id={`sdm_other_role_in_company-required`}
                      label={i18n.fields.sdm_other_role_in_company.label}
                      disabled={
                        fieldsToDisable.includes('sdm_other_role_in_company') &&
                        loggedDataAtIndex === stepperCurrentTicketIdx &&
                        ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                          (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
                      }
                      inputProps={{
                        'aria-label': 'other role in company',
                      }}
                    />
                  )}
                />
                {renderError('sdm_other_role_in_company')}
              </Grid>
            )}

            {handleSubValidation() && (
              <Grid item xs={12} md={6}>
                <Controller
                  name={'sdm_operator_sub_type'}
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={(props) => (
                    <SdmSelectV5
                      options={
                        subOperatorTypeOptions[watch('sdm_operator_type')]
                      }
                      value={props.value}
                      labelKey={'label'}
                      label={i18n.fields.sdm_operator_sub_type.label}
                      disabledLabel={i18n.fields.sdm_operator_sub_type.label}
                      onSelect={(e) => {
                        props.onChange(e);
                      }}
                      customStyle={{
                        width: '100%',
                        marginTop: 2,
                        fontSize: 20,
                      }}
                      disabled={
                        fieldsToDisable.includes('sdm_operator_sub_type') &&
                        loggedDataAtIndex === stepperCurrentTicketIdx &&
                        ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                          (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
                      }
                      inputProps={{
                        'aria-label': 'operator type',
                      }}
                    />
                  )}
                />
                {renderError('sdm_operator_sub_type')}
              </Grid>
            )}
            {ticketHasSubOperator && (
              <Grid item xs={12} md={6}>
                <Controller
                  name={'sdm_other_type'}
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={(props) => (
                    <SdmTextfieldV5
                      variant="standard"
                      style={{ width: '100%' }}
                      onChange={(e) => {
                        props.onChange(e.target.value);
                      }}
                      value={props.value}
                      id={`sdm_other_type-required`}
                      label={i18n.fields.sdm_other_type.label}
                      disabled={
                        fieldsToDisable.includes('sdm_other_type') &&
                        loggedDataAtIndex === stepperCurrentTicketIdx &&
                        ((parsedCurrentTicket?.isPrd && loggedUser.isPrd) ||
                          (!parsedCurrentTicket?.isPrd && !loggedUser.isPrd))
                      }
                      inputProps={{
                        'aria-label': 'other operator type',
                      }}
                    />
                  )}
                />
                {renderError('sdm_other_type')}
              </Grid>
            )}
          </Grid>
          {/* </form> */}
        </Stack>
      ) : null}
    </Box>
  );
};
export default ContainerBody;
